import { useEffect, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';
import { Link } from '../lib/router';
import { whenText } from '../book/recent';

// Recently removed (Kristi, Oct 27): books removed from your list in the last day, each with
// Bring it back (the removal's Undo). A removed book has no page of its own to undo from, so it
// lives in the account menu until Shelves exists. After a day a removed book is gone for good.

type Removed = { id: string; label_number: number; removed_at: string; title: string; action: string | null; back?: boolean };

const label = (n: number) => `B${String(n).padStart(4, '0')}`;

export function RecentlyRemoved({ onClose }: { onClose: () => void }) {
  const [books, setBooks] = useState<Removed[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!supabase) return setBooks([]);
    void (async () => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data, error: e } = await supabase!
        .from('books')
        .select('id, label_number, removed_at, work:works(title)')
        .not('removed_at', 'is', null)
        .gte('removed_at', since)
        .order('removed_at', { ascending: false });
      if (e) return setError(e.message);
      const rows = (data ?? []) as unknown as { id: string; label_number: number; removed_at: string; work: { title: string } | { title: string }[] }[];
      const ids = rows.map((r) => r.id);
      const { data: acts } = ids.length
        ? await supabase!.from('book_actions').select('id, book_id, made_at').in('book_id', ids).eq('summary', 'Book removed').is('undone_at', null).order('made_at', { ascending: false })
        : { data: [] };
      setBooks(
        rows.map((r) => ({
          id: r.id,
          label_number: r.label_number,
          removed_at: r.removed_at,
          title: (Array.isArray(r.work) ? r.work[0]?.title : r.work?.title) ?? label(r.label_number),
          action: ((acts ?? []) as { id: string; book_id: string }[]).find((a) => a.book_id === r.id)?.id ?? null,
        })),
      );
    })();
  }, []);
  const bringBack = async (b: Removed) => {
    if (!supabase || !b.action) return;
    const { error: e } = await supabase.rpc('undo_action', { action: b.action });
    if (e) return setError(e.message);
    setBooks((list) => (list ?? []).map((x) => (x.id === b.id ? { ...x, back: true } : x)));
  };
  return (
    <>
      <button type="button" class="sheet-scrim" aria-label="Close" tabIndex={-1} onClick={onClose} />
      <div class="bk-dialog" role="dialog" aria-modal="true" aria-label="Recently removed" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <h3 class="bk-dialog-title">Recently removed</h3>
        <p class="bk-dialog-sub">Books you removed from your list in the last day. After a day they’re gone for good.</p>
        {error ? <div class="heads-up">Couldn’t do that: {error}</div> : null}
        {books === null ? (
          <p class="bk-quiet">Loading…</p>
        ) : books.length ? (
          <ol class="bk-log">
            {books.map((b) => (
              <li key={b.id}>
                <span class="bk-log-date">{whenText(b.removed_at)}</span>
                <span class="bk-log-text">
                  {b.title} <span class="bk-quiet">{label(b.label_number)}</span>
                </span>
                <span>
                  {b.back ? (
                    <Link href={`/book/${label(b.label_number)}`} class="btn-link">
                      Back on your list · open it
                    </Link>
                  ) : b.action ? (
                    <button type="button" class="btn-secondary" onClick={() => void bringBack(b)}>
                      Bring it back
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p class="bk-quiet">Nothing removed in the last day.</p>
        )}
        <div class="bk-buttons">
          <button type="button" class="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}
